import { useEffect, useMemo, useState } from 'react';
import { Drawer, Button, Space, Input, Typography, Alert, Table, Tag, message, Divider, Empty, Segmented } from 'antd';
import { ReloadOutlined, PlayCircleOutlined, SaveOutlined, BookOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { fetchDataSource, parseData } from '../../services/dataProvider';
import { updateDataSource } from '../../api/dataSources';
import { validateParserText } from '../../utils/parserValidation';
import ParserHelpModal from './ParserHelpModal';
import VisualTransformEditor from './VisualTransformEditor';
import { compileSpecToLegacyParser, validateTransformSpec, type TransformSpec } from '@dtwin/shared';
import type { DataSet } from '@dtwin/shared';

const { Text, Title } = Typography;
const { TextArea } = Input;

export interface DataSourceDebugDrawerProps {
  open: boolean;
  dataset: DataSet | null;
  onClose: () => void;
  onSaved?: () => void;
}

const DEFAULT_PARSER = `{
  "path": "",
  "mapping": [],
  "filter": "",
  "transform": ""
}`;

function pretty(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

export default function DataSourceDebugDrawer({ open, dataset, onClose, onSaved }: DataSourceDebugDrawerProps) {
  const [rawText, setRawText] = useState('');
  const [parserText, setParserText] = useState(DEFAULT_PARSER);
  const [mode, setMode] = useState<'visual' | 'code'>('code');
  const [spec, setSpec] = useState<TransformSpec | null>(null);
  const [result, setResult] = useState<unknown>(undefined);
  const [error, setError] = useState('');
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    if (!open || !dataset) return;
    setRawText('');
    setResult(undefined);
    setError('');
    const p = (dataset as any).parser;
    if (p?.visual) {
      setMode('visual');
      setSpec(p.visual as TransformSpec);
      setParserText(pretty(p));
    } else {
      setMode('code');
      setSpec(null);
      setParserText(p ? pretty(p) : DEFAULT_PARSER);
    }
  }, [open, dataset?.id]);

  const parserError = useMemo(() => validateParserText(parserText), [parserText]);
  const rawSample = useMemo(() => {
    try {
      return rawText.trim() ? JSON.parse(rawText) : undefined;
    } catch {
      return undefined;
    }
  }, [rawText]);
  const visualCheck = useMemo(
    () => (spec ? validateTransformSpec(spec) : { ok: true, errors: [] as string[] }),
    [spec],
  );
  const visualLegacy = useMemo(() => {
    if (!spec) return null;
    try {
      return compileSpecToLegacyParser(spec);
    } catch {
      return null;
    }
  }, [spec]);
  const visualParser = useMemo(
    () => (spec && visualLegacy ? { ...visualLegacy, visual: spec } : null),
    [spec, visualLegacy],
  );
  const canSwitchToVisual = useMemo(() => {
    if (mode === 'visual') return true;
    const t = parserText.trim();
    if (!t || t === DEFAULT_PARSER.trim()) return true;
    if (!visualParser) return false;
    try {
      return JSON.stringify(JSON.parse(t)) === JSON.stringify(visualParser);
    } catch {
      return false;
    }
  }, [mode, parserText, visualParser]);
  const activeParserError = mode === 'visual' ? (visualCheck.ok ? '' : visualCheck.errors.join('；')) : parserError;

  const runParse = (rawStr: string, parserStr: string) => {
    setError('');
    try {
      const parser = parserStr.trim() ? JSON.parse(parserStr) : undefined;
      const raw = rawStr.trim() ? JSON.parse(rawStr) : undefined;
      const out = parseData(raw, parser as any);
      setResult(out);
    } catch (e: any) {
      setResult(undefined);
      setError(e?.message || String(e));
    }
  };

  const activeParserText = () =>
    mode === 'visual' && visualLegacy ? JSON.stringify(visualLegacy) : parserText;

  const handleParse = () => {
    if (mode === 'visual' && !visualCheck.ok) {
      message.warning(visualCheck.errors[0] || '可视化配置不完整');
      return;
    }
    runParse(rawText, activeParserText());
  };

  const switchMode = (next: 'visual' | 'code') => {
    if (next === mode) return;
    if (next === 'visual') {
      if (!canSwitchToVisual) {
        message.warning('当前为自定义 JSON，切回可视化会丢失修改；请保留代码模式或先套用模板重新配置');
        return;
      }
      if (!spec) setSpec({ v: 1, mode: 'rows', steps: [] });
      setMode('visual');
      return;
    }
    if (spec && visualLegacy) setParserText(pretty({ ...visualLegacy, visual: spec }));
    setMode('code');
  };

  const handleSpecChange = (next: TransformSpec) => {
    setSpec(next);
    try {
      setParserText(pretty({ ...compileSpecToLegacyParser(next), visual: next }));
    } catch {
      /* 配置尚不完整时保留上一次文本 */
    }
  };

  const handleFetch = async () => {
    if (!dataset) return;
    setFetching(true);
    setError('');
    try {
      const raw = await fetchDataSource({ type: dataset.type, config: dataset.config });
      setRawText(pretty(raw));
      runParse(pretty(raw), activeParserText());
    } catch (e: any) {
      setError(`取数失败：${e?.message || e}`);
    } finally {
      setFetching(false);
    }
  };

  const handleSave = async () => {
    if (!dataset) return;
    if (mode === 'visual') {
      if (!spec || !visualCheck.ok || !visualLegacy) {
        message.warning(visualCheck.errors[0] || '可视化配置不完整');
        return;
      }
      setSaving(true);
      try {
        await updateDataSource(dataset.id, { parser: { ...visualLegacy, visual: spec } } as any);
        message.success('可视化解析器已保存（引用该数据集的组件立即生效）');
        onSaved?.();
      } catch (e: any) {
        message.error(`保存失败：${e?.message || e}`);
      } finally {
        setSaving(false);
      }
      return;
    }
    if (parserError) { message.warning(parserError); return; }
    setSaving(true);
    try {
      const parser = parserText.trim() ? JSON.parse(parserText) : undefined;
      await updateDataSource(dataset.id, { parser } as any);
      message.success('解析器已保存（引用该数据集的组件立即生效）');
      onSaved?.();
    } catch (e: any) {
      message.error(`保存失败：${e?.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const rows = Array.isArray(result) ? result : result === undefined ? [] : [result];
  const columns = useMemo(() => {
    if (!rows.length) return [];
    const keys = new Set<string>();
    for (const r of rows.slice(0, 20)) {
      if (r && typeof r === 'object') Object.keys(r).forEach((k) => keys.add(k));
    }
    return Array.from(keys).map((k) => ({ title: k, dataIndex: k, key: k, ellipsis: true }));
  }, [result]);

  const numericRows = useMemo(() => {
    if (!Array.isArray(result) || result.length === 0) return null;
    const list = result.filter((r: any) => r && typeof r === 'object' && Number.isFinite(Number(r.value)));
    if (list.length === 0) return null;
    return list.map((r: any) => ({ label: r.label ?? r.name ?? '', value: Number(r.value) }));
  }, [result]);

  return (
    <Drawer
      title={dataset ? `数据源调试 · ${dataset.name}` : '数据源调试'}
      open={open}
      onClose={onClose}
      width={980}
      destroyOnHidden
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} loading={fetching} onClick={handleFetch} data-testid="ds-debug-fetch">
            拉取一条
          </Button>
          <Button
            icon={<PlayCircleOutlined />}
            disabled={!!activeParserError}
            onClick={handleParse}
            data-testid="ds-debug-parse"
          >
            解析
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            disabled={!!activeParserError}
            onClick={handleSave}
            data-testid="ds-debug-save"
          >
            保存解析器
          </Button>
        </Space>
      }
    >
      {dataset && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          类型：{dataset.type} · 连接与查询在「编辑数据集」里配置；本页只调试「原始报文 → 解析器 → 解析结果」。
          MQTT 每次「拉取一条」会临时订阅并等待一条消息（约 9s 超时）。
        </Text>
      )}
      <Divider style={{ margin: '12px 0' }} />

      <Title level={5} style={{ marginTop: 0 }}>① 原始报文</Title>
      <TextArea
        value={rawText}
        onChange={(e) => setRawText(e.target.value)}
        rows={7}
        placeholder='点击右上「拉取一条」，或粘贴样例 JSON（如 {"data":{"points":{"temp":7.5}}}）'
        style={{ fontFamily: 'Consolas, monospace', fontSize: 12 }}
        data-testid="ds-debug-raw"
      />

      <Divider style={{ margin: '12px 0' }} />
      <Space align="center" style={{ marginBottom: 6 }} wrap>
        <Title level={5} style={{ marginTop: 0, marginBottom: 0 }}>② 解析器</Title>
        <Segmented
          size="small"
          value={mode}
          onChange={(v) => switchMode(v as 'visual' | 'code')}
          options={[{ value: 'visual', label: '可视化（推荐）' }, { value: 'code', label: '代码（高级）' }]}
          data-testid="ds-parser-mode"
        />
        <Button
          type="text"
          size="small"
          icon={<BookOutlined />}
          onClick={() => setHelpOpen(true)}
          data-testid="ds-parser-help"
          title="解析器使用说明与示例"
        />
      </Space>
      {mode === 'visual' ? (
        <>
          <VisualTransformEditor
            mode="rows"
            spec={spec ?? { v: 1, mode: 'rows', steps: [] }}
            onChange={handleSpecChange}
            sample={rawSample}
          />
          {activeParserError && (
            <Alert type="error" showIcon message={activeParserError} style={{ marginTop: 8 }} data-testid="ds-debug-parser-error" />
          )}
          {!spec && (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 8 }}
              message="当前数据集还没有可视化配置：套用模板或点样例节点添加字段，保存后即生效。"
            />
          )}
          {visualLegacy && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: 'pointer', fontSize: 12, color: '#888' }}>查看生成的 JSON（只读）</summary>
              <pre style={{ fontSize: 12, maxHeight: 200, overflow: 'auto', background: 'rgba(255,255,255,0.06)', color: '#e6e6e6', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4, padding: 8 }}>
                {pretty({ ...visualLegacy, visual: spec })}
              </pre>
            </details>
          )}
        </>
      ) : (
        <>
          <Text type="secondary" style={{ fontSize: 12 }}>
            path=取值路径（a.b.c / a[0].b）；mapping=字段映射；filter=过滤表达式；transform=JS 转换（入参 data，如 data.map(x=&gt;({'{'}label:x.ts,value:x.v{'}'}))）
          </Text>
          <TextArea
            value={parserText}
            onChange={(e) => setParserText(e.target.value)}
            rows={8}
            style={{ fontFamily: 'Consolas, monospace', fontSize: 12, marginTop: 6 }}
            data-testid="ds-debug-parser"
          />
          {parserError && <Alert type="error" showIcon message={parserError} style={{ marginTop: 8 }} data-testid="ds-debug-parser-error" />}
        </>
      )}

      <Divider style={{ margin: '12px 0' }} />
      <Title level={5} style={{ marginTop: 0 }}>③ 解析结果</Title>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 8 }} data-testid="ds-debug-error" />}
      {result === undefined && !error ? (
        <Empty description="尚未解析（点击「解析」或「拉取一条」）" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <>
          <Space size={8} style={{ marginBottom: 8 }}>
            <Tag color="blue">{Array.isArray(result) ? `数组 · ${result.length} 行` : '对象/标量'}</Tag>
            <Text type="secondary" style={{ fontSize: 12 }} data-testid="ds-debug-summary">
              {Array.isArray(result) ? `rows=${result.length}` : `type=${typeof result}`}
            </Text>
          </Space>
          {numericRows && (
            <ReactECharts
              style={{ height: 220, marginBottom: 8 }}
              option={{
                grid: { left: 40, right: 16, top: 20, bottom: 28 },
                xAxis: { type: 'category', data: numericRows.map((r) => r.label) },
                yAxis: { type: 'value' },
                series: [{ type: 'line', smooth: true, data: numericRows.map((r) => r.value), areaStyle: { opacity: 0.12 } }],
              }}
              notMerge
              data-testid="ds-debug-chart"
            />
          )}
          <Table
            size="small"
            rowKey={(_, i) => String(i)}
            dataSource={rows.slice(0, 50) as any[]}
            columns={columns}
            pagination={false}
            scroll={{ x: 'max-content' }}
            data-testid="ds-debug-result"
          />
          <details style={{ marginTop: 8 }}>
            <summary style={{ cursor: 'pointer', fontSize: 12, color: '#888' }}>查看解析结果 JSON</summary>
            <pre style={{ fontSize: 12, maxHeight: 240, overflow: 'auto', background: 'rgba(255,255,255,0.06)', color: '#e6e6e6', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4, padding: 8 }} data-testid="ds-debug-result-json">
              {pretty(result)}
            </pre>
          </details>
        </>
      )}

      <ParserHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </Drawer>
  );
}
